export class PageSlice {
  /** 1부터 시작하는 page의 구간만 잘라 준다. 범위를 벗어난 page는 마지막 페이지로 맞춘다. */
  static of<T>(
    items: ReadonlyArray<T>,
    page: number,
    pageSize: number,
  ): { items: T[]; page: number; totalPages: number } {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const current = Math.min(Math.max(1, page), totalPages);
    return { items: items.slice((current - 1) * pageSize, current * pageSize), page: current, totalPages };
  }
}

/** 현재 페이지 주변만 번호를 보이고 먼 구간은 줄임표로 접는다. */
function pageNumbers(page: number, totalPages: number): Array<number | 'gap-start' | 'gap-end'> {
  const keep = new Set([1, totalPages, page - 1, page, page + 1]);
  const result: Array<number | 'gap-start' | 'gap-end'> = [];
  let previous = 0;
  for (let number = 1; number <= totalPages; number += 1) {
    if (!keep.has(number)) continue;
    if (number - previous > 1) result.push(previous === 1 ? 'gap-start' : 'gap-end');
    result.push(number);
    previous = number;
  }
  return result;
}

const BUTTON = 'min-w-8 rounded-md border px-2 py-1 text-xs';

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
}): React.JSX.Element | null {
  if (totalPages <= 1) return null;

  return (
    <nav className="mt-4 flex items-center justify-center gap-1" aria-label="페이지 이동">
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className={`${BUTTON} border-line bg-surface text-muted hover:bg-line-soft disabled:cursor-default disabled:opacity-40`}
      >
        ‹ 이전
      </button>
      {pageNumbers(page, totalPages).map((entry) =>
        typeof entry === 'string' ? (
          <span key={entry} className="px-1 text-xs text-faint">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            aria-current={entry === page ? 'page' : undefined}
            onClick={() => onChange(entry)}
            className={`${BUTTON} ${
              entry === page
                ? 'border-accent-line bg-accent-soft font-semibold text-accent'
                : 'border-line bg-surface text-muted hover:bg-line-soft'
            }`}
          >
            {entry}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        className={`${BUTTON} border-line bg-surface text-muted hover:bg-line-soft disabled:cursor-default disabled:opacity-40`}
      >
        다음 ›
      </button>
    </nav>
  );
}
